import { ReactTable } from "@aix/ui"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react"
import { useEffect, useState } from "react"

import { trpc } from "../../lib/trpc"

function isValidJobId(jobId: string): boolean {
  // UUIDs are typically 36 characters including dashes (8-4-4-4-12 format)
  return jobId.length >= 32 && jobId.length <= 40
}

function extractContentData(contentStr: string) {
  try {
    const content = JSON.parse(contentStr)

    // Handle different Twitter API response structures
    const mediaUrls = []

    // Extended entities media (Twitter API v1.1)
    if (content?.extended_entities?.media) {
      mediaUrls.push(
        ...content.extended_entities.media.map((m: any) => ({
          type: m.type || "photo",
          url: m.media_url_https || m.media_url || m.url,
          thumbnail: m.media_url_https || m.media_url || m.url,
        })),
      )
    }

    // Entities media (fallback)
    if (content?.entities?.media && mediaUrls.length === 0) {
      mediaUrls.push(
        ...content.entities.media.map((m: any) => ({
          type: m.type || "photo",
          url: m.media_url_https || m.media_url || m.url,
          thumbnail: m.media_url_https || m.media_url || m.url,
        })),
      )
    }

    // Direct media array (newer API structures)
    if (content?.media && mediaUrls.length === 0) {
      mediaUrls.push(
        ...content.media.map((m: any) => ({
          type: m.type || "photo",
          url: m.media_url_https || m.media_url || m.url,
          thumbnail: m.media_url_https || m.media_url || m.url,
        })),
      )
    }

    return {
      text: content?.text || content?.full_text || content?.content || "",
      mediaUrls,
      author: content?.author || content?.user || {},
      createdAt: content?.created_at || content?.createdAt,
      isRetweet: !!(content?.retweeted_status || content?.retweet),
      originalTweet: content?.retweeted_status || content?.retweet,
      urls: content?.entities?.urls || content?.urls || [],
    }
  } catch {
    return {
      text: contentStr,
      mediaUrls: [],
      author: {},
      createdAt: "",
      isRetweet: false,
      originalTweet: null,
      urls: [],
    }
  }
}

function ContentPreview({ result }: { result: any }) {
  const contentData = extractContentData(result.content || "{}")

  return (
    <div className="border-t border-gray-100">
      <div className="px-4 pb-4 pt-2 space-y-4">
        {/* Tweet Text */}
        {contentData.text && (
          <div className="bg-foregroundx rounded-lg p-3">
            <div className="text-sm font-medium text-primary mb-2">Tweet Text:</div>
            <div className="text-sm  whitespace-pre-wrap">{contentData.text}</div>
          </div>
        )}

        {/* Media */}
        {contentData.mediaUrls.length > 0 && (
          <div>
            <div className="text-sm font-medium text-primary mb-2">Media:</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {contentData.mediaUrls.map((media: any, idx: number) => (
                <div key={idx} className="relative group">
                  <img
                    src={media.thumbnail}
                    alt={`Media ${idx + 1}`}
                    className="w-full h-24 object-cover rounded-lg border border-border"
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).style.display = "none"
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all rounded-lg flex items-center justify-center">
                    <a
                      href={media.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-0 group-hover:opacity-100 bg-background rounded-full p-2 transition-opacity"
                    >
                      <ExternalLink className="w-4 h-4 text-primary" />
                    </a>
                  </div>
                  <div className="absolute top-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    {media.type}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* URLs */}
        {contentData.urls.length > 0 && (
          <div>
            <div className="text-sm font-medium text-primary mb-2">Links:</div>
            <div className="space-y-2">
              {contentData.urls.map((url: any, idx: number) => (
                <a
                  key={idx}
                  href={url.expanded_url || url.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:text-blue-700 underline break-all"
                >
                  {url.display_url || url.expanded_url || url.url}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Retweet Info */}
        {contentData.isRetweet && contentData.originalTweet && (
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-sm font-medium text-blue-700 mb-1">Retweeted:</div>
            <div className="text-sm text-blue-900">
              {contentData.originalTweet.text || contentData.originalTweet.full_text}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function JobResultsTab() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { jobId?: string }
  const [jobId, setJobId] = useState("")
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // Set jobId from query params
  useEffect(() => {
    if (search.jobId && search.jobId !== jobId) {
      setJobId(search.jobId)
    }
  }, [search.jobId])

  const { data, isLoading, error } = trpc.scraping.listResults.useQuery(
    { jobId: jobId || undefined, platform: "twitter", limit: 50 },
    {
      keepPreviousData: true,
      enabled: !!jobId && isValidJobId(jobId),
    },
  )

  const handleJobIdChange = (newJobId: string) => {
    setJobId(newJobId)
    setExpandedRows(new Set()) // Reset expanded rows when changing jobs
    navigate({
      to: "/scraping",
      // @ts-ignore
      search: { ...search, tab: "results", jobId: newJobId || undefined },
    })
  }

  const toggleRowExpansion = (resultId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(resultId)) {
        newSet.delete(resultId)
      } else {
        newSet.add(resultId)
      }
      return newSet
    })
  }

  const renderContent = () => {
    if (!jobId) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-500 mb-2">No job ID specified</div>
          <div className="text-sm text-gray-400">Please enter a job ID above to view results</div>
        </div>
      )
    }

    if (!isValidJobId(jobId)) {
      return (
        <div className="text-center py-12">
          <div className="text-destructive mb-2">Invalid job ID format</div>
          <div className="text-sm text-gray-400">Please enter a proper job ID (32-40 characters)</div>
        </div>
      )
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
            <span className="text-primary">Loading results...</span>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="text-destructive mb-2">Error loading results</div>
          <div className="text-sm text-gray-400">{error.message}</div>
        </div>
      )
    }

    return (
      <div className="bg-background border border-border rounded-lg overflow-hidden">
        {/* Results Table */}
        <div className="overflow-x-auto">
          {(data || []).map((r: any) => {
            const contentData = extractContentData(r.content || "{}")
            const isExpanded = expandedRows.has(r.id)

            return (
              <div key={r.id} className="border-b border-gray-100 last:border-b-0">
                {/* Main Row */}
                <div className="p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                    {/* Author & Time */}
                    <div className="lg:col-span-2">
                      <div className="text-sm font-medium ">{r.author || "Unknown"}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(r.createdAt).toLocaleDateString()}{" "}
                        {new Date(r.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>

                    {/* Content Preview */}
                    <div className="lg:col-span-6">
                      <div
                        className="text-sm  overflow-hidden"
                        style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                      >
                        {contentData.text || "(No text content)"}
                      </div>
                      {contentData.mediaUrls.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex -space-x-2">
                            {contentData.mediaUrls.slice(0, 3).map((media: any, idx: number) => (
                              <img
                                key={idx}
                                src={media.thumbnail}
                                alt=""
                                className="w-8 h-8 rounded border-2 border-white object-cover"
                                onError={(e) => {
                                  ;(e.target as HTMLImageElement).style.display = "none"
                                }}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-blue-600">{contentData.mediaUrls.length} media</span>
                        </div>
                      )}
                    </div>

                    {/* Metrics */}
                    <div className="lg:col-span-2">
                      {(() => {
                        try {
                          const metrics = JSON.parse(r.metrics || "{}")
                          return (
                            <div className="flex gap-3 text-xs text-primary">
                              <div className="flex items-center gap-1">
                                <span>❤️</span>
                                <span>{metrics.favorites || 0}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span>🔄</span>
                                <span>{metrics.retweets || 0}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span>💬</span>
                                <span>{metrics.replies || 0}</span>
                              </div>
                            </div>
                          )
                        } catch {
                          return <div className="text-xs text-gray-400">No metrics</div>
                        }
                      })()}
                    </div>

                    {/* Actions */}
                    <div className="lg:col-span-2 flex items-center gap-2">
                      {r.sourceUrl && (
                        <a
                          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                          href={r.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View
                        </a>
                      )}
                      <button
                        onClick={() => toggleRowExpansion(r.id)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-foregroundx text-primary  transition-colors"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronDown className="w-3 h-3 mr-1" />
                            Hide
                          </>
                        ) : (
                          <>
                            <ChevronRight className="w-3 h-3 mr-1" />
                            Details
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expandable Content */}
                {isExpanded && <ContentPreview result={r} />}
              </div>
            )
          })}
        </div>

        {/* No results message */}
        {(!data || data.length === 0) && (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-2">No results found</div>
            <div className="text-sm text-gray-400">
              This job may not have completed yet or found no matching content
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold  mb-2">Job Results</h2>
        <p className="text-sm text-primary">View scraped results for a specific job</p>
      </div>

      <div className="bg-foregroundx p-4 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-primary mb-2">Job ID</label>
          <input
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
            value={jobId}
            onChange={(e) => handleJobIdChange(e.target.value)}
            placeholder="Enter job ID to view results..."
          />
          {jobId && !isValidJobId(jobId) && (
            <div className="mt-2 text-sm text-destructive">Job ID should be 32-40 characters long (UUID format)</div>
          )}
        </div>
      </div>

      {renderContent()}
    </div>
  )
}
